import React, { forwardRef, useImperativeHandle, useRef } from 'react';
import { Stage, Layer, Image as KonvaImage, Rect as KonvaRect } from 'react-konva';
import ShapeRenderer from '../shapes/ShapeRenderer';
import { TOOLS } from '../constants/drawingTools';

/**
 * PrintStage - Reusable Konva Stage component for rendering pages for print
 * This component mirrors the rendering structure from Replan.js but is designed
 * for off-screen rendering and export to image.
 * 
 * @param {Object} konvaImage - HTMLImageElement of the page
 * @param {Object} imageSize - { width, height } dimensions
 * @param {Array} shapes - Array of shape objects to render
 * @param {number} dpi - Document DPI
 * @param {Object} currentScaleProps - Scale properties for measurements
 * @param {React.Ref} ref - Forwarded ref to access the Stage
 */
const PrintStage = forwardRef(({
  konvaImage,
  imageSize,
  shapes = [],
  dpi,
  drawingScale,
  currentScaleProps,
  displayKeys = [],
  displayUnitSystem = null,
  fontSizeScale = 1,
}, forwardedRef) => {
  const stageRef = useRef(null);
  
  // Expose the stage instance through the forwarded ref
  useImperativeHandle(forwardedRef, () => stageRef.current);

  // Filter and deduplicate shapes
  const validShapes = shapes.filter(shape => shape && shape.id && shape.type);

  return (
    <Stage
      ref={stageRef}
      width={imageSize.width}
      height={imageSize.height}
    >
      {/* PDF Background Layer */}
      <Layer name="image-layer" listening={false}>
        {konvaImage && (
          <>
            <KonvaRect
              name="background-rect"
              x={0}
              y={0}
              width={imageSize.width}
              height={imageSize.height}
              fill={getComputedStyle(document.documentElement)
                .getPropertyValue('--drawing-background-color')
                .trim()}
              listening={false}
            />
            <KonvaImage
              name="background-image"
              image={konvaImage}
              width={imageSize.width}
              height={imageSize.height}
              listening={false}
            />
          </>
        )}
      </Layer>

      {/* Drawing Layer - Render all shapes without selection/interaction */}
      <Layer 
        name="drawing-layer"
        scaleX={drawingScale || 1}
        scaleY={drawingScale || 1}
      >
        {validShapes.map((shape) => (
          <ShapeRenderer
            key={shape.id}
            shape={shape}
            isSelected={false} // No selection in print mode
            selectedShapeIds={[]} // Always show all text in print
            selectedTool={TOOLS.SELECT} // Neutral tool
            stageScale={(drawingScale || 1) / fontSizeScale} // Mirror the visual layer scale so renderer computes sizes correctly
            stageRef={stageRef}
            onShapeClick={() => {}} // No interaction
            onShapeDragEnd={() => {}}
            onVertexClick={() => {}}
            onTransformEnd={() => {}}
            currentScaleProps={currentScaleProps}
            dpi={dpi} // Use image DPI if available, otherwise fallback to provided DPI
            imageSize={imageSize}
            isDraggingMode={false}
            vertexEditInfo={null}
            allShapes={validShapes} // Pass all shapes for MeasurementSummaryShape calculations
            onUpdateShape={() => {}} // No shape updates in print mode
            displayKeys={displayKeys}
            displayUnitSystem={displayUnitSystem}
          />
        ))}
      </Layer>
    </Stage>
  );
});

PrintStage.displayName = 'PrintStage';

export default PrintStage;
